import { useProjectId } from '@/hooks/useProjectId.ts'
import { gql, useQuery } from '@apollo/client'

export const useQueryAnalyticsIterations = (apiKey: string | null) => {
  const projectId = useProjectId()
  const { loading, data } = useQuery<{
    iterations: IterationSummary[]
  }>(ITERATIONS_SUMMARIES_GQL, {
    context: {
      headers: {
        'X-API-KEY': apiKey,
      },
    },
    variables: {
      projectId,
      limit: 9,
    },
    fetchPolicy: 'no-cache',
    skip: !projectId || !apiKey,
  })

  return { loading, iterations: data?.iterations ?? [] }
}

export const useQueryAnalyticsReleaseFlags = (apiKey: string | null, status: StoryStatus) => {
  const projectId = useProjectId()
  const { loading, data } = useQuery<{
    releases: ReleaseFlag[]
  }>(RELEASE_FLAGS_GQL, {
    context: {
      headers: {
        'X-API-KEY': apiKey,
      },
    },
    variables: {
      projectId,
      status,
    },
    fetchPolicy: 'no-cache',
    skip: !projectId || !apiKey,
  })

  return { loading, data }
}

export const useQueryAllProjectsLimitedIterations = (apiKey: string | null) => {
  const { loading, data } = useQuery<{ projects: ProjectWithIterations[] }>(ALL_PROJECT_LIMITED_ITERATIONS_GQL, {
    context: {
      headers: {
        'X-API-KEY': apiKey,
      },
    },
    variables: {
      limit: 6,
    },
    fetchPolicy: 'no-cache',
    skip: !apiKey,
  })

  const allIterations: ProjectCardIteration[] = data?.projects?.flatMap((project) => project.iterations) || []

  return { loading, iterations: allIterations }
}

const ITERATIONS_SUMMARIES_GQL = gql`
    query AnalyticsIterationSummaries($projectId: ID!, $limit: Int!) {
        iterations(projectId: $projectId, limit: $limit) {
            projectId
            number
            acceptedPoints
            effectivePoints
            finish
            points
            start
            teamStrength
            velocity
            volatility
            stories {
                id
                title
                storyType
                status
                estimate
                acceptedAt
                cycleTimeDetails {
                    totalCycleTime
                    startedTime
                    rejectedTime
                    finishedTime
                    deliveredTime
                    rejectedCount
                }
                labels {
                    name
                    id
                }
                owners {
                    id
                    name
                }
            }
            analytics {
                rejectionRate
            }
        }
    }
`

const RELEASE_FLAGS_GQL = gql`
    query AnalyticsReleaseFlags($projectId: ID!, $status: StoryStatus!) {
        releases(projectId: $projectId, status: $status) {
            acceptedAt
            countsAccepted
            countsTotal
            currentState
            deadline
            id
            name
            pointsAccepted
            pointsTotal
            projectedCompletionInterval
            stories {
                id
                owners {
                    email
                    id
                    initials
                    name
                }
                labels {
                    id
                    name
                }
                acceptedAt
                afterId
                beforeId
                estimate
                status
                storyType
                title
            }
        }
    }
`

const ALL_PROJECT_LIMITED_ITERATIONS_GQL = gql`
    query AnalyticsProjectsIterationSummary($limit: Int) {
        projects {
            iterations(limit: $limit) {
                number
                start
                finish
                teamStrength
                velocity
                acceptedPoints
                volatility
                projectId
            }
        }
    }
`
